import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { authService, notificationService, warmCoreCaches } from "@/services/api";
import { requestPushToken, onForegroundPush } from "@/lib/firebase";
import { connectSocket, disconnectSocket } from "@/lib/socket";

const AuthContext = createContext(null);

// Best-effort: a user who declines the browser permission prompt, or a
// browser without push support, should never block login or app usage —
// this only ever logs, never throws into the caller.
function registerPushToken() {
  requestPushToken()
    .then((token) => token && notificationService.registerFcmToken(token))
    .catch((err) => console.warn("Push notification setup skipped:", err.message));
}

// requestPushToken() also doubles as "get the current token": permission is
// already granted at this point, so it resolves the existing token without
// showing a new prompt — same call as registration, different purpose.
async function unregisterPushToken() {
  try {
    const token = await requestPushToken();
    if (token) await notificationService.unregisterFcmToken(token);
  } catch (err) {
    console.warn("Push token cleanup skipped:", err.message);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => authService.current());
  const [ready, setReady] = useState(true);

  const login = useCallback(async (creds) => {
    const u = await authService.login(creds);
    setUser(u);
    registerPushToken();
    connectSocket();
    return u;
  }, []);

  const logout = useCallback(async () => {
    // Must unregister before clearing the token — the DELETE call needs the
    // Authorization header authService.logout() is about to remove.
    await unregisterPushToken();
    disconnectSocket();
    authService.logout();
    setUser(null);
  }, []);

  useEffect(() => {
    setReady(true);
    // On a page refresh (session restored from localStorage rather than a
    // fresh login() call), the in-memory vehicle/driver/trip/alert caches
    // start out empty — warm them so synchronous lookups elsewhere have
    // something to read as soon as possible.
    if (authService.current()) {
      warmCoreCaches();
      registerPushToken();
      connectSocket();
    }
  }, []);

  useEffect(() => {
    // A push that arrives while a tab is open and focused is delivered here,
    // not to the service worker (that path only fires when backgrounded/
    // closed — see firebase-messaging-sw.js's onBackgroundMessage). Without
    // this, a foreground push is received by the SDK but never shown —
    // silently invisible, which is what made "sending succeeds but nothing
    // appears" hard to tell apart from an actual delivery failure.
    if (!user) return undefined;
    let unsubscribe = () => {};
    let cancelled = false;
    onForegroundPush((payload) => {
      const title = payload.notification?.title || "ARC Beacon";
      const body = payload.notification?.body || "";
      toast(title, { description: body || undefined });
    }).then((fn) => {
      if (cancelled) fn();
      else unsubscribe = fn;
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, ready, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
